import config from "appConfig";

const theory = ` <h3 class=sectionHead>Matrices for linear quadrupoles</h3>
<p>
   A quadrupole is a system having two ”ports” i.e. two inputs, one of which may optionally be considered as an output (see <a href="#" data-img-src=${config.ASSETS_URL}/01/29/theory/figure-1.svg data-img-alt="Figure 1. Two-port system.">Figure 1</a>). In the context of electrical circuits, each input having two poles a two-port is called quadrupole.
   <figure class=figure>
<p><img src=${config.ASSETS_URL}/01/29/theory/figure-1.svg alt="Figure 1. Two-port system."><figcaption class=caption>Figure 1. Two-port system.</figcaption></figure>
<p>A quadrupole being linear, there is a linear relationship between the input variables and the output variables.
<h4 class=subsectionHead>Transfer matrix for pressure and volume velocity</h4>
<p>
   For the couples pressure and volume velocity
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mo class=MathClass-open>(</mo>
      <mi>p</mi>
      <mo class=MathClass-punc>,</mo>
      <mi>U</mi>
      <mo class=MathClass-close>)</mo>
   </math>, we can write
<table class=equation>
   <tr>
      <td>
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <mrow>
               <mo fence=true form=prefix>(</mo>
               <mrow>
                  <mtable align=axis style="" equalrows=false columnlines="none none none none none none none none none" equalcolumns=false class=array>
                     <mtr>
                        <mtd class=array columnalign=center>
                           <msub>
                              <mrow>
                                 <mi>p</mi>
                              </mrow>
                              <mrow>
                                 <mn>1</mn>
                              </mrow>
                           </msub>
                        </mtd>
                     </mtr>
                     <mtr>
                        <mtd class=array columnalign=center>
                           <msub>
                              <mrow>
                                 <mi>U</mi>
                              </mrow>
                              <mrow>
                                 <mn>1</mn>
                              </mrow>
                           </msub>
                        </mtd>
                     </mtr>
                  </mtable>
               </mrow>
               <mo fence=true form=postfix>)</mo>
            </mrow>
            <mo class=MathClass-rel>=</mo>
            <mrow>
               <mo fence=true form=prefix>(</mo>
               <mrow>
                  <mtable align=axis style="" equalrows=false columnlines="none none none none none none none none none" equalcolumns=false class=array>
                     <mtr>
                        <mtd class=array columnalign=center>
                           <mi>A</mi>
                        </mtd>
                        <mtd class=array columnalign=center>
                           <mi>B</mi>
                        </mtd>
                     </mtr>
                     <mtr>
                        <mtd class=array columnalign=center>
                           <mi>C</mi>
                        </mtd>
                        <mtd class=array columnalign=center>
                           <mi>D</mi>
                        </mtd>
                     </mtr>
                  </mtable>
               </mrow>
               <mo fence=true form=postfix>)</mo>
            </mrow>
            <mrow>
               <mo fence=true form=prefix>(</mo>
               <mrow>
                  <mtable align=axis style="" equalrows=false columnlines="none none none none none none none none none" equalcolumns=false class=array>
                     <mtr>
                        <mtd class=array columnalign=center>
                           <msub>
                              <mrow>
                                 <mi>p</mi>
                              </mrow>
                              <mrow>
                                 <mn>2</mn>
                              </mrow>
                           </msub>
                        </mtd>
                     </mtr>
                     <mtr>
                        <mtd class=array columnalign=center>
                           <mo class=MathClass-bin>−</mo>
                           <msub>
                              <mrow>
                                 <mi>U</mi>
                              </mrow>
                              <mrow>
                                 <mn>2</mn>
                              </mrow>
                           </msub>
                        </mtd>
                     </mtr>
                  </mtable>
               </mrow>
               <mo fence=true form=postfix>)</mo>
            </mrow>
         </math>
      <td class=eq-no>(1)
</table>
<p>where 
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mrow>
         <mo fence=true form=prefix>(</mo>
         <mrow>
            <mtable>
               <mtr class=smallmatrix>
                  <mtd>
                     <mi>A</mi>
                  </mtd>
                  <mtd>
                     <mi>B</mi>
                  </mtd>
               </mtr>
               <mtr class=smallmatrix>
                  <mtd>
                     <mi>C</mi>
                  </mtd>
                  <mtd>
                     <mi>D</mi>
                  </mtd>
               </mtr>
            </mtable>
         </mrow>
         <mo fence=true form=postfix>)</mo>
      </mrow>
   </math>
   is the so called transfer matrix or chain matrix. The orientations for the volume velocities
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>U</mi>
   </math>
   are directed to the quadrupole. So
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mo class=MathClass-bin>−</mo>
      <msub>
         <mrow>
            <mi>U</mi>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
      </msub>
   </math>
   is the volume velocity going out of the quadrupole on the right. The transfer matrices can be chained because when two quadrupoles are connected together there is continuity of pressure and volume velocity at the junction. The flow
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mo class=MathClass-bin>−</mo>
      <msub>
         <mrow>
            <mi>U</mi>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
      </msub>
   </math>
   getting out of the two-port is also the flow going in the next two-port. So, in order to calculate the transfer matrix of a quadrupole network in series, it is simply required to multiply the transfer matrices of each quadrupole.
<h4 class=subsectionHead>Impedance and admittance matrix</h4>
<p>It can be chosen to make the link between pressures and volume velocities. This define the impedance matrix
<table class=equation>
   <tr>
      <td>
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <mrow>
               <mo fence=true form=prefix>(</mo>
               <mrow>
                  <mtable align=axis style="" equalrows=false columnlines="none none none none none none none none none" equalcolumns=false class=array>
                     <mtr>
                        <mtd class=array columnalign=center>
                           <msub>
                              <mrow>
                                 <mi>p</mi>
                              </mrow>
                              <mrow>
                                 <mn>1</mn>
                              </mrow>
                           </msub>
                        </mtd>
                     </mtr>
                     <mtr>
                        <mtd class=array columnalign=center>
                           <msub>
                              <mrow>
                                 <mi>p</mi>
                              </mrow>
                              <mrow>
                                 <mn>2</mn>
                              </mrow>
                           </msub>
                        </mtd>
                     </mtr>
                  </mtable>
               </mrow>
               <mo fence=true form=postfix>)</mo>
            </mrow>
            <mo class=MathClass-rel>=</mo>
            <mrow>
               <mo fence=true form=prefix>(</mo>
               <mrow>
                  <mtable align=axis style="" equalrows=false columnlines="none none none none none none none none none" equalcolumns=false class=array>
                     <mtr>
                        <mtd class=array columnalign=center>
                           <msub>
                              <mrow>
                                 <mi>Z</mi>
                              </mrow>
                              <mrow>
                                 <mn>1</mn>
                                 <mn>1</mn>
                              </mrow>
                           </msub>
                        </mtd>
                        <mtd class=array columnalign=center>
                           <msub>
                              <mrow>
                                 <mi>Z</mi>
                              </mrow>
                              <mrow>
                                 <mn>1</mn>
                                 <mn>2</mn>
                              </mrow>
                           </msub>
                        </mtd>
                     </mtr>
                     <mtr>
                        <mtd class=array columnalign=center>
                           <msub>
                              <mrow>
                                 <mi>Z</mi>
                              </mrow>
                              <mrow>
                                 <mn>2</mn>
                                 <mn>1</mn>
                              </mrow>
                           </msub>
                        </mtd>
                        <mtd class=array columnalign=center>
                           <msub>
                              <mrow>
                                 <mi>Z</mi>
                              </mrow>
                              <mrow>
                                 <mn>2</mn>
                                 <mn>2</mn>
                              </mrow>
                           </msub>
                        </mtd>
                     </mtr>
                  </mtable>
               </mrow>
               <mo fence=true form=postfix>)</mo>
            </mrow>
            <mrow>
               <mo fence=true form=prefix>(</mo>
               <mrow>
                  <mtable align=axis style="" equalrows=false columnlines="none none none none none none none none none" equalcolumns=false class=array>
                     <mtr>
                        <mtd class=array columnalign=center>
                           <msub>
                              <mrow>
                                 <mi>U</mi>
                              </mrow>
                              <mrow>
                                 <mn>1</mn>
                              </mrow>
                           </msub>
                        </mtd>
                     </mtr>
                     <mtr>
                        <mtd class=array columnalign=center>
                           <msub>
                              <mrow>
                                 <mi>U</mi>
                              </mrow>
                              <mrow>
                                 <mn>2</mn>
                              </mrow>
                           </msub>
                        </mtd>
                     </mtr>
                  </mtable>
               </mrow>
               <mo fence=true form=postfix>)</mo>
            </mrow>
         </math>
      <td class=eq-no>(2)
</table>
<p>or the admittance matrix
<table class=equation>
   <tr>
      <td>
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <mrow>
               <mo fence=true form=prefix>(</mo>
               <mrow>
                  <mtable align=axis style="" equalrows=false columnlines="none none none none none none none none none" equalcolumns=false class=array>
                     <mtr>
                        <mtd class=array columnalign=center>
                           <msub>
                              <mrow>
                                 <mi>U</mi>
                              </mrow>
                              <mrow>
                                 <mn>1</mn>
                              </mrow>
                           </msub>
                        </mtd>
                     </mtr>
                     <mtr>
                        <mtd class=array columnalign=center>
                           <msub>
                              <mrow>
                                 <mi>U</mi>
                              </mrow>
                              <mrow>
                                 <mn>2</mn>
                              </mrow>
                           </msub>
                        </mtd>
                     </mtr>
                  </mtable>
               </mrow>
               <mo fence=true form=postfix>)</mo>
            </mrow>
            <mo class=MathClass-rel>=</mo>
            <mrow>
               <mo fence=true form=prefix>(</mo>
               <mrow>
                  <mtable align=axis style="" equalrows=false columnlines="none none none none none none none none none" equalcolumns=false class=array>
                     <mtr>
                        <mtd class=array columnalign=center>
                           <msub>
                              <mrow>
                                 <mi>Y</mi>
                              </mrow>
                              <mrow>
                                 <mn>1</mn>
                                 <mn>1</mn>
                              </mrow>
                           </msub>
                        </mtd>
                        <mtd class=array columnalign=center>
                           <msub>
                              <mrow>
                                 <mi>Y</mi>
                              </mrow>
                              <mrow>
                                 <mn>1</mn>
                                 <mn>2</mn>
                              </mrow>
                           </msub>
                        </mtd>
                     </mtr>
                     <mtr>
                        <mtd class=array columnalign=center>
                           <msub>
                              <mrow>
                                 <mi>Y</mi>
                              </mrow>
                              <mrow>
                                 <mn>2</mn>
                                 <mn>1</mn>
                              </mrow>
                           </msub>
                        </mtd>
                        <mtd class=array columnalign=center>
                           <msub>
                              <mrow>
                                 <mi>Y</mi>
                              </mrow>
                              <mrow>
                                 <mn>2</mn>
                                 <mn>2</mn>
                              </mrow>
                           </msub>
                        </mtd>
                     </mtr>
                  </mtable>
               </mrow>
               <mo fence=true form=postfix>)</mo>
            </mrow>
            <mrow>
               <mo fence=true form=prefix>(</mo>
               <mrow>
                  <mtable align=axis style="" equalrows=false columnlines="none none none none none none none none none" equalcolumns=false class=array>
                     <mtr>
                        <mtd class=array columnalign=center>
                           <msub>
                              <mrow>
                                 <mi>p</mi>
                              </mrow>
                              <mrow>
                                 <mn>1</mn>
                              </mrow>
                           </msub>
                        </mtd>
                     </mtr>
                     <mtr>
                        <mtd class=array columnalign=center>
                           <msub>
                              <mrow>
                                 <mi>p</mi>
                              </mrow>
                              <mrow>
                                 <mn>2</mn>
                              </mrow>
                           </msub>
                        </mtd>
                     </mtr>
                  </mtable>
               </mrow>
               <mo fence=true form=postfix>)</mo>
            </mrow>
         </math>
      <td class=eq-no>(3)
</table>
<p>Impedance and admittance matrix can be generalized to a N-port. It that case these are NxN matrices.
<h4 class=subsectionHead>Scattering matrix</h4>
<p>
   The scattering matrix connects the ingoing waves and the outgoing waves (scattered) as shown in <a href="#" data-img-src=${config.ASSETS_URL}/01/29/theory/figure-2.svg data-img-alt="Figure 2. Waves scattered by a two-port system.">Figure 2</a>.
   <figure class=figure>
<p><img src=${config.ASSETS_URL}/01/29/theory/figure-2.svg alt="Figure 2. Waves scattered by a two-port system." class=graphics><figcaption class=caption>Figure 2. Waves scattered by a two-port system.</figcaption></figure>
<p>
   Setting
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msubsup>
         <mrow>
            <mi>p</mi>
         </mrow>
         <mrow>
            <mn>1</mn>
         </mrow>
         <mrow>
            <mo class=MathClass-bin>+</mo>
         </mrow>
      </msubsup>
      <mo class=MathClass-rel>=</mo>
      <mfrac>
         <mrow>
            <msub>
               <mrow>
                  <mi>p</mi>
               </mrow>
               <mrow>
                  <mn>1</mn>
               </mrow>
            </msub>
            <mo class=MathClass-bin>+</mo>
            <msub>
               <mrow>
                  <mi>Z</mi>
               </mrow>
               <mrow>
                  <mi>c</mi>
                  <mn>1</mn>
               </mrow>
            </msub>
            <msub>
               <mrow>
                  <mi>U</mi>
               </mrow>
               <mrow>
                  <mn>1</mn>
               </mrow>
            </msub>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
      </mfrac>
      <mi>P</mi>
   </math>
   and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msubsup>
         <mrow>
            <mi>p</mi>
         </mrow>
         <mrow>
            <mn>1</mn>
         </mrow>
         <mrow>
            <mo class=MathClass-bin>−</mo>
         </mrow>
      </msubsup>
      <mo class=MathClass-rel>=</mo>
      <mfrac>
         <mrow>
            <msub>
               <mrow>
                  <mi>p</mi>
               </mrow>
               <mrow>
                  <mn>1</mn>
               </mrow>
            </msub>
            <mo class=MathClass-bin>−</mo>
            <msub>
               <mrow>
                  <mi>Z</mi>
               </mrow>
               <mrow>
                  <mi>c</mi>
                  <mn>2</mn>
               </mrow>
            </msub>
            <msub>
               <mrow>
                  <mi>U</mi>
               </mrow>
               <mrow>
                  <mn>1</mn>
               </mrow>
            </msub>
         </mrow>
         <mrow>
            <mn>1</mn>
         </mrow>
      </mfrac>
      <mi>P</mi>
   </math>
   and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msubsup>
         <mrow>
            <mi>p</mi>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
         <mrow>
            <mo class=MathClass-bin>+</mo>
         </mrow>
      </msubsup>
      <mo class=MathClass-rel>=</mo>
      <mfrac>
         <mrow>
            <msub>
               <mrow>
                  <mi>p</mi>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </msub>
            <mo class=MathClass-bin>−</mo>
            <msub>
               <mrow>
                  <mi>Z</mi>
               </mrow>
               <mrow>
                  <mi>c</mi>
                  <mn>2</mn>
               </mrow>
            </msub>
            <msub>
               <mrow>
                  <mi>U</mi>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </msub>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
      </mfrac>
      <mi>P</mi>
   </math>
   and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msubsup>
         <mrow>
            <mi>p</mi>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
         <mrow>
            <mo class=MathClass-bin>−</mo>
         </mrow>
      </msubsup>
      <mo class=MathClass-rel>=</mo>
      <mfrac>
         <mrow>
            <msub>
               <mrow>
                  <mi>p</mi>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </msub>
            <mo class=MathClass-bin>+</mo>
            <msub>
               <mrow>
                  <mi>Z</mi>
               </mrow>
               <mrow>
                  <mi>c</mi>
                  <mn>2</mn>
               </mrow>
            </msub>
            <msub>
               <mrow>
                  <mi>U</mi>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </msub>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
      </mfrac>
      <mi>P</mi>
   </math>
   ,
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msubsup>
         <mrow>
            <mi>p</mi>
         </mrow>
         <mrow>
            <mn>1</mn>
         </mrow>
         <mrow>
            <mo class=MathClass-bin>+</mo>
         </mrow>
      </msubsup>
   </math>
   and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msubsup>
         <mrow>
            <mi>p</mi>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
         <mrow>
            <mo class=MathClass-bin>−</mo>
         </mrow>
      </msubsup>
   </math>
   are ingoing waves converging to the dipole and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msubsup>
         <mrow>
            <mi>p</mi>
         </mrow>
         <mrow>
            <mn>1</mn>
         </mrow>
         <mrow>
            <mo class=MathClass-bin>−</mo>
         </mrow>
      </msubsup>
   </math>
   and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msubsup>
         <mrow>
            <mi>p</mi>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
         <mrow>
            <mo class=MathClass-bin>+</mo>
         </mrow>
      </msubsup>
   </math>
   are outgoing waves scattered by the dipole. The scattering matrix is defined by
<table class=equation>
   <tr>
      <td>
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <mrow>
               <mo fence=true form=prefix>(</mo>
               <mrow>
                  <mtable align=axis style="" equalrows=false columnlines="none none none none none none none none none" equalcolumns=false class=array>
                     <mtr>
                        <mtd class=array columnalign=center>
                           <msubsup>
                              <mrow>
                                 <mi>p</mi>
                              </mrow>
                              <mrow>
                                 <mn>1</mn>
                              </mrow>
                              <mrow>
                                 <mo class=MathClass-bin>−</mo>
                              </mrow>
                           </msubsup>
                        </mtd>
                     </mtr>
                     <mtr>
                        <mtd class=array columnalign=center>
                           <msubsup>
                              <mrow>
                                 <mi>p</mi>
                              </mrow>
                              <mrow>
                                 <mn>2</mn>
                              </mrow>
                              <mrow>
                                 <mo class=MathClass-bin>+</mo>
                              </mrow>
                           </msubsup>
                        </mtd>
                     </mtr>
                  </mtable>
               </mrow>
               <mo fence=true form=postfix>)</mo>
            </mrow>
            <mo class=MathClass-rel>=</mo>
            <mrow>
               <mo fence=true form=prefix>(</mo>
               <mrow>
                  <mtable align=axis style="" equalrows=false columnlines="none none none none none none none none none" equalcolumns=false class=array>
                     <mtr>
                        <mtd class=array columnalign=center>
                           <msub>
                              <mrow>
                                 <mi>R</mi>
                              </mrow>
                              <mrow>
                                 <mn>1</mn>
                              </mrow>
                           </msub>
                        </mtd>
                        <mtd class=array columnalign=center>
                           <msub>
                              <mrow>
                                 <mi>T</mi>
                              </mrow>
                              <mrow>
                                 <mn>2</mn>
                                 <mn>1</mn>
                              </mrow>
                           </msub>
                        </mtd>
                     </mtr>
                     <mtr>
                        <mtd class=array columnalign=center>
                           <msub>
                              <mrow>
                                 <mi>T</mi>
                              </mrow>
                              <mrow>
                                 <mn>1</mn>
                                 <mn>2</mn>
                              </mrow>
                           </msub>
                        </mtd>
                        <mtd class=array columnalign=center>
                           <msub>
                              <mrow>
                                 <mi>R</mi>
                              </mrow>
                              <mrow>
                                 <mn>2</mn>
                              </mrow>
                           </msub>
                        </mtd>
                     </mtr>
                  </mtable>
               </mrow>
               <mo fence=true form=postfix>)</mo>
            </mrow>
            <mrow>
               <mo fence=true form=prefix>(</mo>
               <mrow>
                  <mtable align=axis style="" equalrows=false columnlines="none none none none none none none none none" equalcolumns=false class=array>
                     <mtr>
                        <mtd class=array columnalign=center>
                           <msubsup>
                              <mrow>
                                 <mi>p</mi>
                              </mrow>
                              <mrow>
                                 <mn>1</mn>
                              </mrow>
                              <mrow>
                                 <mo class=MathClass-bin>+</mo>
                              </mrow>
                           </msubsup>
                        </mtd>
                     </mtr>
                     <mtr>
                        <mtd class=array columnalign=center>
                           <msubsup>
                              <mrow>
                                 <mi>p</mi>
                              </mrow>
                              <mrow>
                                 <mn>2</mn>
                              </mrow>
                              <mrow>
                                 <mo class=MathClass-bin>−</mo>
                              </mrow>
                           </msubsup>
                        </mtd>
                     </mtr>
                  </mtable>
               </mrow>
               <mo fence=true form=postfix>)</mo>
            </mrow>
         </math>
      <td class=eq-no>(4)
</table>
<p>
   This matrix contains transmission and reflection coefficients for an incident wave from one side (<math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msubsup>
         <mrow>
            <mi>p</mi>
         </mrow>
         <mrow>
            <mn>1</mn>
         </mrow>
         <mrow>
            <mo class=MathClass-bin>+</mo>
         </mrow>
      </msubsup>
   </math>
   reflected with a coefficient
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>R</mi>
         </mrow>
         <mrow>
            <mn>1</mn>
         </mrow>
      </msub>
   </math>
   and transmitted with a coefficient
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>T</mi>
         </mrow>
         <mrow>
            <mn>1</mn>
            <mn>2</mn>
         </mrow>
      </msub>
   </math>) and the other (<math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msubsup>
         <mrow>
            <mi>p</mi>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
         <mrow>
            <mo class=MathClass-bin>−</mo>
         </mrow>
      </msubsup>
   </math>
   reflected with a coefficient
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>R</mi>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
      </msub>
   </math>
   and transmitted with a coefficient
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>T</mi>
         </mrow>
         <mrow>
            <mn>2</mn>
            <mn>1</mn>
         </mrow>
      </msub>
   </math>). Scattering matrices can be generalized to the case of multiport starting from symmetrical orientation. The scattering matrix is sometime called
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>S</mi>
   </math>
   matrix and denoted
   <strong>
      <mstyle class=text>
         <mtext>S</mtext>
      </mstyle>
   </strong>
   <mo class=MathClass-rel>=</mo>
   <mo class=MathClass-open>(</mo>
   <msub>
      <mrow>
         <mi>S</mi>
      </mrow>
      <mrow>
         <mi>i</mi>
         <mi>j</mi>
      </mrow>
   </msub>
   <mo class=MathClass-close>)</mo>.
<h4 class=subsectionHead>From one matrix to another</h4>
<p>
   The formula to go from the transfer matrix
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mrow>
         <mo fence=true form=prefix>(</mo>
         <mrow>
            <mtable>
               <mtr class=smallmatrix>
                  <mtd>
                     <mi>A</mi>
                  </mtd>
                  <mtd>
                     <mi>B</mi>
                  </mtd>
               </mtr>
               <mtr class=smallmatrix>
                  <mtd>
                     <mi>C</mi>
                  </mtd>
                  <mtd>
                     <mi>D</mi>
                  </mtd>
               </mtr>
            </mtable>
         </mrow>
         <mo fence=true form=postfix>)</mo>
      </mrow>
   </math>
   to the scattering matrix
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mrow>
         <mo fence=true form=prefix>(</mo>
         <mrow>
            <mtable>
               <mtr class=smallmatrix>
                  <mtd>
                     <msub>
                        <mrow>
                           <mi>R</mi>
                        </mrow>
                        <mrow>
                           <mn>1</mn>
                        </mrow>
                     </msub>
                  </mtd>
                  <mtd>
                     <msub>
                        <mrow>
                           <mi>T</mi>
                        </mrow>
                        <mrow>
                           <mn>2</mn>
                           <mn>1</mn>
                        </mrow>
                     </msub>
                  </mtd>
               </mtr>
               <mtr class=smallmatrix>
                  <mtd>
                     <msub>
                        <mrow>
                           <mi>T</mi>
                        </mrow>
                        <mrow>
                           <mn>1</mn>
                           <mn>2</mn>
                        </mrow>
                     </msub>
                  </mtd>
                  <mtd>
                     <msub>
                        <mrow>
                           <mi>R</mi>
                        </mrow>
                        <mrow>
                           <mn>2</mn>
                        </mrow>
                     </msub>
                  </mtd>
               </mtr>
            </mtable>
         </mrow>
         <mo fence=true form=postfix>)</mo>
      </mrow>
   </math>, in the case where
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>Z</mi>
         </mrow>
         <mrow>
            <mi>c</mi>
            <mn>1</mn>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <msub>
         <mrow>
            <mi>Z</mi>
         </mrow>
         <mrow>
            <mi>c</mi>
            <mn>2</mn>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <msub>
         <mrow>
            <mi>Z</mi>
         </mrow>
         <mrow>
            <mi>c</mi>
         </mrow>
      </msub>
   </math>
   , is the following
<table class=equation>
   <tr>
      <td style="font-size: 9px">
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <mrow>
               <mo fence=true form=prefix>(</mo>
               <mrow>
                  <mtable align=axis style="" equalrows=false columnlines="none none none none none none none none none" equalcolumns=false class=array>
                     <mtr>
                        <mtd class=array columnalign=center>
                           <msub>
                              <mrow>
                                 <mi>R</mi>
                              </mrow>
                              <mrow>
                                 <mn>1</mn>
                              </mrow>
                           </msub>
                        </mtd>
                        <mtd class=array columnalign=center>
                           <msub>
                              <mrow>
                                 <mi>T</mi>
                              </mrow>
                              <mrow>
                                 <mn>2</mn>
                                 <mn>1</mn>
                              </mrow>
                           </msub>
                        </mtd>
                     </mtr>
                     <mtr>
                        <mtd class=array columnalign=center>
                           <msub>
                              <mrow>
                                 <mi>T</mi>
                              </mrow>
                              <mrow>
                                 <mn>1</mn>
                                 <mn>2</mn>
                              </mrow>
                           </msub>
                        </mtd>
                        <mtd class=array columnalign=center>
                           <msub>
                              <mrow>
                                 <mi>R</mi>
                              </mrow>
                              <mrow>
                                 <mn>2</mn>
                              </mrow>
                           </msub>
                        </mtd>
                     </mtr>
                  </mtable>
               </mrow>
               <mo fence=true form=postfix>)</mo>
            </mrow>
            <mo class=MathClass-rel>=</mo>
            <mrow>
               <mo fence=true form=prefix>(</mo>
               <mrow>
                  <mtable align=axis style="" equalrows=false columnlines="none none none none none none none none none" equalcolumns=false class=array>
                     <mtr>
                        <mtd class=array columnalign=center>
                           <mfrac>
                              <mrow>
                                 <mi>A</mi>
                                 <mo class=MathClass-bin>+</mo>
                                 <mi>B</mi>
                                 <mo class=MathClass-bin>∕</mo>
                                 <msub>
                                    <mrow>
                                       <mi>Z</mi>
                                    </mrow>
                                    <mrow>
                                       <mi>c</mi>
                                    </mrow>
                                 </msub>
                                 <mo class=MathClass-bin>−</mo>
                                 <mi>C</mi>
                                 <msub>
                                    <mrow>
                                       <mi>Z</mi>
                                    </mrow>
                                    <mrow>
                                       <mi>c</mi>
                                    </mrow>
                                 </msub>
                                 <mo class=MathClass-bin>−</mo>
                                 <mi>D</mi>
                              </mrow>
                              <mrow>
                                 <mi>A</mi>
                                 <mo class=MathClass-bin>+</mo>
                                 <mi>B</mi>
                                 <mo class=MathClass-bin>∕</mo>
                                 <msub>
                                    <mrow>
                                       <mi>Z</mi>
                                    </mrow>
                                    <mrow>
                                       <mi>c</mi>
                                    </mrow>
                                 </msub>
                                 <mo class=MathClass-bin>+</mo>
                                 <mi>C</mi>
                                 <msub>
                                    <mrow>
                                       <mi>Z</mi>
                                    </mrow>
                                    <mrow>
                                       <mi>c</mi>
                                    </mrow>
                                 </msub>
                                 <mo class=MathClass-bin>+</mo>
                                 <mi>D</mi>
                              </mrow>
                           </mfrac>
                        </mtd>
                        <mtd class=array columnalign=center>
                           <mfrac>
                              <mrow>
                                 <mn>2</mn>
                                 <mo class=MathClass-open>(</mo>
                                 <mi>A</mi>
                                 <mi>D</mi>
                                 <mo class=MathClass-bin>−</mo>
                                 <mi>B</mi>
                                 <mi>C</mi>
                                 <mo class=MathClass-close>)</mo>
                              </mrow>
                              <mrow>
                                 <mi>A</mi>
                                 <mo class=MathClass-bin>+</mo>
                                 <mi>B</mi>
                                 <mo class=MathClass-bin>∕</mo>
                                 <msub>
                                    <mrow>
                                       <mi>Z</mi>
                                    </mrow>
                                    <mrow>
                                       <mi>c</mi>
                                    </mrow>
                                 </msub>
                                 <mo class=MathClass-bin>+</mo>
                                 <mi>C</mi>
                                 <msub>
                                    <mrow>
                                       <mi>Z</mi>
                                    </mrow>
                                    <mrow>
                                       <mi>c</mi>
                                    </mrow>
                                 </msub>
                                 <mo class=MathClass-bin>+</mo>
                                 <mi>D</mi>
                              </mrow>
                           </mfrac>
                        </mtd>
                     </mtr>
                     <mtr>
                        <mtd class=array columnalign=center>
                           <mfrac>
                              <mrow>
                                 <mn>2</mn>
                              </mrow>
                              <mrow>
                                 <mi>A</mi>
                                 <mo class=MathClass-bin>+</mo>
                                 <mi>B</mi>
                                 <mo class=MathClass-bin>∕</mo>
                                 <msub>
                                    <mrow>
                                       <mi>Z</mi>
                                    </mrow>
                                    <mrow>
                                       <mi>c</mi>
                                    </mrow>
                                 </msub>
                                 <mo class=MathClass-bin>+</mo>
                                 <mi>C</mi>
                                 <msub>
                                    <mrow>
                                       <mi>Z</mi>
                                    </mrow>
                                    <mrow>
                                       <mi>c</mi>
                                    </mrow>
                                 </msub>
                                 <mo class=MathClass-bin>+</mo>
                                 <mi>D</mi>
                              </mrow>
                           </mfrac>
                        </mtd>
                        <mtd class=array columnalign=center>
                           <mfrac>
                              <mrow>
                                 <mo class=MathClass-bin>−</mo>
                                 <mi>A</mi>
                                 <mo class=MathClass-bin>+</mo>
                                 <mi>B</mi>
                                 <mo class=MathClass-bin>∕</mo>
                                 <msub>
                                    <mrow>
                                       <mi>Z</mi>
                                    </mrow>
                                    <mrow>
                                       <mi>c</mi>
                                    </mrow>
                                 </msub>
                                 <mo class=MathClass-bin>−</mo>
                                 <mi>C</mi>
                                 <msub>
                                    <mrow>
                                       <mi>Z</mi>
                                    </mrow>
                                    <mrow>
                                       <mi>c</mi>
                                    </mrow>
                                 </msub>
                                 <mo class=MathClass-bin>+</mo>
                                 <mi>D</mi>
                              </mrow>
                              <mrow>
                                 <mi>A</mi>
                                 <mo class=MathClass-bin>+</mo>
                                 <mi>B</mi>
                                 <mo class=MathClass-bin>∕</mo>
                                 <msub>
                                    <mrow>
                                       <mi>Z</mi>
                                    </mrow>
                                    <mrow>
                                       <mi>c</mi>
                                    </mrow>
                                 </msub>
                                 <mo class=MathClass-bin>+</mo>
                                 <mi>C</mi>
                                 <msub>
                                    <mrow>
                                       <mi>Z</mi>
                                    </mrow>
                                    <mrow>
                                       <mi>c</mi>
                                    </mrow>
                                 </msub>
                                 <mo class=MathClass-bin>+</mo>
                                 <mi>D</mi>
                              </mrow>
                           </mfrac>
                        </mtd>
                     </mtr>
                  </mtable>
               </mrow>
               <mo fence=true form=postfix>)</mo>
            </mrow>
         </math>
      <td class=eq-no>(5)
</table>
<p>and invertly
<table class=equation>
   <tr>
      <td style="font-size: 4px">
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <mrow>
               <mo fence=true form=prefix>(</mo>
               <mrow>
                  <mtable align=axis style="" equalrows=false columnlines="none none none none none none none none none" equalcolumns=false class=array>
                     <mtr>
                        <mtd class=array columnalign=center>
                           <mi>A</mi>
                        </mtd>
                        <mtd class=array columnalign=center>
                           <mi>B</mi>
                        </mtd>
                     </mtr>
                     <mtr>
                        <mtd class=array columnalign=center>
                           <mi>C</mi>
                        </mtd>
                        <mtd class=array columnalign=center>
                           <mi>D</mi>
                        </mtd>
                     </mtr>
                  </mtable>
               </mrow>
               <mo fence=true form=postfix>)</mo>
            </mrow>
            <mo class=MathClass-rel>=</mo>
            <mrow>
               <mo fence=true form=prefix>(</mo>
               <mrow>
                  <mtable align=axis style="" equalrows=false columnlines="none none none none none none none none none" equalcolumns=false class=array>
                     <mtr>
                        <mtd class=array columnalign=center>
                           <mfrac>
                              <mrow>
                                 <mo class=MathClass-open>(</mo>
                                 <mn>1</mn>
                                 <mo class=MathClass-bin>+</mo>
                                 <msub>
                                    <mrow>
                                       <mi>R</mi>
                                    </mrow>
                                    <mrow>
                                       <mn>1</mn>
                                    </mrow>
                                 </msub>
                                 <mo class=MathClass-close>)</mo>
                                 <mo class=MathClass-open>(</mo>
                                 <mn>1</mn>
                                 <mo class=MathClass-bin>−</mo>
                                 <msub>
                                    <mrow>
                                       <mi>R</mi>
                                    </mrow>
                                    <mrow>
                                       <mn>2</mn>
                                    </mrow>
                                 </msub>
                                 <mo class=MathClass-close>)</mo>
                                 <mo class=MathClass-bin>+</mo>
                                 <msub>
                                    <mrow>
                                       <mi>T</mi>
                                    </mrow>
                                    <mrow>
                                       <mn>2</mn>
                                       <mn>1</mn>
                                    </mrow>
                                 </msub>
                                 <msub>
                                    <mrow>
                                       <mi>T</mi>
                                    </mrow>
                                    <mrow>
                                       <mn>1</mn>
                                       <mn>2</mn>
                                    </mrow>
                                 </msub>
                              </mrow>
                              <mrow>
                                 <mn>2</mn>
                                 <msub>
                                    <mrow>
                                       <mi>T</mi>
                                    </mrow>
                                    <mrow>
                                       <mn>1</mn>
                                       <mn>2</mn>
                                    </mrow>
                                 </msub>
                              </mrow>
                           </mfrac>
                        </mtd>
                        <mtd class=array columnalign=center>
                           <msub>
                              <mrow>
                                 <mi>Z</mi>
                              </mrow>
                              <mrow>
                                 <mi>c</mi>
                              </mrow>
                           </msub>
                           <mfrac>
                              <mrow>
                                 <mo class=MathClass-open>(</mo>
                                 <mn>1</mn>
                                 <mo class=MathClass-bin>+</mo>
                                 <msub>
                                    <mrow>
                                       <mi>R</mi>
                                    </mrow>
                                    <mrow>
                                       <mn>1</mn>
                                    </mrow>
                                 </msub>
                                 <mo class=MathClass-close>)</mo>
                                 <mo class=MathClass-open>(</mo>
                                 <mn>1</mn>
                                 <mo class=MathClass-bin>+</mo>
                                 <msub>
                                    <mrow>
                                       <mi>R</mi>
                                    </mrow>
                                    <mrow>
                                       <mn>2</mn>
                                    </mrow>
                                 </msub>
                                 <mo class=MathClass-close>)</mo>
                                 <mo class=MathClass-bin>−</mo>
                                 <msub>
                                    <mrow>
                                       <mi>T</mi>
                                    </mrow>
                                    <mrow>
                                       <mn>2</mn>
                                       <mn>1</mn>
                                    </mrow>
                                 </msub>
                                 <msub>
                                    <mrow>
                                       <mi>T</mi>
                                    </mrow>
                                    <mrow>
                                       <mn>1</mn>
                                       <mn>2</mn>
                                    </mrow>
                                 </msub>
                              </mrow>
                              <mrow>
                                 <mn>2</mn>
                                 <msub>
                                    <mrow>
                                       <mi>T</mi>
                                    </mrow>
                                    <mrow>
                                       <mn>1</mn>
                                       <mn>2</mn>
                                    </mrow>
                                 </msub>
                              </mrow>
                           </mfrac>
                        </mtd>
                     </mtr>
                     <mtr>
                        <mtd class=array columnalign=center>
                           <mfrac>
                              <mrow>
                                 <mn>1</mn>
                              </mrow>
                              <mrow>
                                 <msub>
                                    <mrow>
                                       <mi>Z</mi>
                                    </mrow>
                                    <mrow>
                                       <mi>c</mi>
                                    </mrow>
                                 </msub>
                              </mrow>
                           </mfrac>
                           <mfrac>
                              <mrow>
                                 <mo class=MathClass-open>(</mo>
                                 <mn>1</mn>
                                 <mo class=MathClass-bin>−</mo>
                                 <msub>
                                    <mrow>
                                       <mi>R</mi>
                                    </mrow>
                                    <mrow>
                                       <mn>1</mn>
                                    </mrow>
                                 </msub>
                                 <mo class=MathClass-close>)</mo>
                                 <mo class=MathClass-open>(</mo>
                                 <mn>1</mn>
                                 <mo class=MathClass-bin>−</mo>
                                 <msub>
                                    <mrow>
                                       <mi>R</mi>
                                    </mrow>
                                    <mrow>
                                       <mn>2</mn>
                                    </mrow>
                                 </msub>
                                 <mo class=MathClass-close>)</mo>
                                 <mo class=MathClass-bin>−</mo>
                                 <msub>
                                    <mrow>
                                       <mi>T</mi>
                                    </mrow>
                                    <mrow>
                                       <mn>2</mn>
                                       <mn>1</mn>
                                    </mrow>
                                 </msub>
                                 <msub>
                                    <mrow>
                                       <mi>T</mi>
                                    </mrow>
                                    <mrow>
                                       <mn>1</mn>
                                       <mn>2</mn>
                                    </mrow>
                                 </msub>
                              </mrow>
                              <mrow>
                                 <mn>2</mn>
                                 <msub>
                                    <mrow>
                                       <mi>T</mi>
                                    </mrow>
                                    <mrow>
                                       <mn>1</mn>
                                       <mn>2</mn>
                                    </mrow>
                                 </msub>
                              </mrow>
                           </mfrac>
                        </mtd>
                        <mtd class=array columnalign=center>
                           <mfrac>
                              <mrow>
                                 <mo class=MathClass-open>(</mo>
                                 <mn>1</mn>
                                 <mo class=MathClass-bin>−</mo>
                                 <msub>
                                    <mrow>
                                       <mi>R</mi>
                                    </mrow>
                                    <mrow>
                                       <mn>1</mn>
                                    </mrow>
                                 </msub>
                                 <mo class=MathClass-close>)</mo>
                                 <mo class=MathClass-open>(</mo>
                                 <mn>1</mn>
                                 <mo class=MathClass-bin>+</mo>
                                 <msub>
                                    <mrow>
                                       <mi>R</mi>
                                    </mrow>
                                    <mrow>
                                       <mn>2</mn>
                                    </mrow>
                                 </msub>
                                 <mo class=MathClass-close>)</mo>
                                 <mo class=MathClass-bin>+</mo>
                                 <msub>
                                    <mrow>
                                       <mi>T</mi>
                                    </mrow>
                                    <mrow>
                                       <mn>2</mn>
                                       <mn>1</mn>
                                    </mrow>
                                 </msub>
                                 <msub>
                                    <mrow>
                                       <mi>T</mi>
                                    </mrow>
                                    <mrow>
                                       <mn>1</mn>
                                       <mn>2</mn>
                                    </mrow>
                                 </msub>
                              </mrow>
                              <mrow>
                                 <mn>2</mn>
                                 <msub>
                                    <mrow>
                                       <mi>T</mi>
                                    </mrow>
                                    <mrow>
                                       <mn>1</mn>
                                       <mn>2</mn>
                                    </mrow>
                                 </msub>
                              </mrow>
                           </mfrac>
                        </mtd>
                     </mtr>
                  </mtable>
               </mrow>
               <mo fence=true form=postfix>)</mo>
            </mrow>
            <mo class=MathClass-punc>.</mo>
         </math>
      <td class=eq-no>(6)
</table>
<p>
   The formula to go from the transfer matrix
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mrow>
         <mo fence=true form=prefix>(</mo>
         <mrow>
            <mtable>
               <mtr class=smallmatrix>
                  <mtd>
                     <mi>A</mi>
                  </mtd>
                  <mtd>
                     <mi>B</mi>
                  </mtd>
               </mtr>
               <mtr class=smallmatrix>
                  <mtd>
                     <mi>C</mi>
                  </mtd>
                  <mtd>
                     <mi>D</mi>
                  </mtd>
               </mtr>
            </mtable>
         </mrow>
         <mo fence=true form=postfix>)</mo>
      </mrow>
   </math>
   to the impedance matrix
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mrow>
         <mo fence=true form=prefix>(</mo>
         <mrow>
            <mtable>
               <mtr class=smallmatrix>
                  <mtd>
                     <msub>
                        <mrow>
                           <mi>Z</mi>
                        </mrow>
                        <mrow>
                           <mn>1</mn>
                           <mn>1</mn>
                        </mrow>
                     </msub>
                  </mtd>
                  <mtd>
                     <msub>
                        <mrow>
                           <mi>Z</mi>
                        </mrow>
                        <mrow>
                           <mn>1</mn>
                           <mn>2</mn>
                        </mrow>
                     </msub>
                  </mtd>
               </mtr>
               <mtr class=smallmatrix>
                  <mtd>
                     <msub>
                        <mrow>
                           <mi>Z</mi>
                        </mrow>
                        <mrow>
                           <mn>2</mn>
                           <mn>1</mn>
                        </mrow>
                     </msub>
                  </mtd>
                  <mtd>
                     <msub>
                        <mrow>
                           <mi>Z</mi>
                        </mrow>
                        <mrow>
                           <mn>2</mn>
                           <mn>2</mn>
                        </mrow>
                     </msub>
                  </mtd>
               </mtr>
            </mtable>
         </mrow>
         <mo fence=true form=postfix>)</mo>
      </mrow>
   </math>
   is the following
<table class=equation>
   <tr>
      <td>
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <mrow>
               <mo fence=true form=prefix>(</mo>
               <mrow>
                  <mtable align=axis style="" equalrows=false columnlines="none none none none none none none none none" equalcolumns=false class=array>
                     <mtr>
                        <mtd class=array columnalign=center>
                           <msub>
                              <mrow>
                                 <mi>Z</mi>
                              </mrow>
                              <mrow>
                                 <mn>1</mn>
                                 <mn>1</mn>
                              </mrow>
                           </msub>
                        </mtd>
                        <mtd class=array columnalign=center>
                           <msub>
                              <mrow>
                                 <mi>Z</mi>
                              </mrow>
                              <mrow>
                                 <mn>1</mn>
                                 <mn>2</mn>
                              </mrow>
                           </msub>
                        </mtd>
                     </mtr>
                     <mtr>
                        <mtd class=array columnalign=center>
                           <msub>
                              <mrow>
                                 <mi>Z</mi>
                              </mrow>
                              <mrow>
                                 <mn>2</mn>
                                 <mn>1</mn>
                              </mrow>
                           </msub>
                        </mtd>
                        <mtd class=array columnalign=center>
                           <msub>
                              <mrow>
                                 <mi>Z</mi>
                              </mrow>
                              <mrow>
                                 <mn>2</mn>
                                 <mn>2</mn>
                              </mrow>
                           </msub>
                        </mtd>
                     </mtr>
                  </mtable>
               </mrow>
               <mo fence=true form=postfix>)</mo>
            </mrow>
            <mo class=MathClass-rel>=</mo>
            <mrow>
               <mo fence=true form=prefix>(</mo>
               <mrow>
                  <mtable align=axis style="" equalrows=false columnlines="none none none none none none none none none" equalcolumns=false class=array>
                     <mtr>
                        <mtd class=array columnalign=center>
                           <mfrac>
                              <mrow>
                                 <mi>A</mi>
                              </mrow>
                              <mrow>
                                 <mi>C</mi>
                              </mrow>
                           </mfrac>
                        </mtd>
                        <mtd class=array columnalign=center>
                           <mfrac>
                              <mrow>
                                 <mi>A</mi>
                                 <mi>D</mi>
                                 <mo class=MathClass-bin>−</mo>
                                 <mi>B</mi>
                                 <mi>C</mi>
                              </mrow>
                              <mrow>
                                 <mi>C</mi>
                              </mrow>
                           </mfrac>
                        </mtd>
                     </mtr>
                     <mtr>
                        <mtd class=array columnalign=center>
                           <mfrac>
                              <mrow>
                                 <mn>1</mn>
                              </mrow>
                              <mrow>
                                 <mi>C</mi>
                              </mrow>
                           </mfrac>
                        </mtd>
                        <mtd class=array columnalign=center>
                           <mfrac>
                              <mrow>
                                 <mi>D</mi>
                              </mrow>
                              <mrow>
                                 <mi>C</mi>
                              </mrow>
                           </mfrac>
                        </mtd>
                     </mtr>
                  </mtable>
               </mrow>
               <mo fence=true form=postfix>)</mo>
            </mrow>
         </math>
      <td class=eq-no>(7)
</table>
<p>and invertly
<table class=equation>
   <tr>
      <td style="font-size: 14px">
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <mrow>
               <mo fence=true form=prefix>(</mo>
               <mrow>
                  <mtable align=axis style="" equalrows=false columnlines="none none none none none none none none none" equalcolumns=false class=array>
                     <mtr>
                        <mtd class=array columnalign=center>
                           <mi>A</mi>
                        </mtd>
                        <mtd class=array columnalign=center>
                           <mi>B</mi>
                        </mtd>
                     </mtr>
                     <mtr>
                        <mtd class=array columnalign=center>
                           <mi>C</mi>
                        </mtd>
                        <mtd class=array columnalign=center>
                           <mi>D</mi>
                        </mtd>
                     </mtr>
                  </mtable>
               </mrow>
               <mo fence=true form=postfix>)</mo>
            </mrow>
            <mo class=MathClass-rel>=</mo>
            <mrow>
               <mo fence=true form=prefix>(</mo>
               <mrow>
                  <mtable align=axis style="" equalrows=false columnlines="none none none none none none none none none" equalcolumns=false class=array>
                     <mtr>
                        <mtd class=array columnalign=center>
                           <mfrac>
                              <mrow>
                                 <msub>
                                    <mrow>
                                       <mi>Z</mi>
                                    </mrow>
                                    <mrow>
                                       <mn>1</mn>
                                       <mn>1</mn>
                                    </mrow>
                                 </msub>
                              </mrow>
                              <mrow>
                                 <msub>
                                    <mrow>
                                       <mi>Z</mi>
                                    </mrow>
                                    <mrow>
                                       <mn>2</mn>
                                       <mn>1</mn>
                                    </mrow>
                                 </msub>
                              </mrow>
                           </mfrac>
                        </mtd>
                        <mtd class=array columnalign=center>
                           <mfrac>
                              <mrow>
                                 <msub>
                                    <mrow>
                                       <mi>Z</mi>
                                    </mrow>
                                    <mrow>
                                       <mn>1</mn>
                                       <mn>1</mn>
                                    </mrow>
                                 </msub>
                                 <msub>
                                    <mrow>
                                       <mi>Z</mi>
                                    </mrow>
                                    <mrow>
                                       <mn>2</mn>
                                       <mn>2</mn>
                                    </mrow>
                                 </msub>
                                 <mo class=MathClass-bin>−</mo>
                                 <msub>
                                    <mrow>
                                       <mi>Z</mi>
                                    </mrow>
                                    <mrow>
                                       <mn>1</mn>
                                       <mn>2</mn>
                                    </mrow>
                                 </msub>
                                 <msub>
                                    <mrow>
                                       <mi>Z</mi>
                                    </mrow>
                                    <mrow>
                                       <mn>2</mn>
                                       <mn>1</mn>
                                    </mrow>
                                 </msub>
                              </mrow>
                              <mrow>
                                 <msub>
                                    <mrow>
                                       <mi>Z</mi>
                                    </mrow>
                                    <mrow>
                                       <mn>2</mn>
                                       <mn>1</mn>
                                    </mrow>
                                 </msub>
                              </mrow>
                           </mfrac>
                        </mtd>
                     </mtr>
                     <mtr>
                        <mtd class=array columnalign=center>
                           <mfrac>
                              <mrow>
                                 <mn>1</mn>
                              </mrow>
                              <mrow>
                                 <msub>
                                    <mrow>
                                       <mi>Z</mi>
                                    </mrow>
                                    <mrow>
                                       <mn>2</mn>
                                       <mn>1</mn>
                                    </mrow>
                                 </msub>
                              </mrow>
                           </mfrac>
                        </mtd>
                        <mtd class=array columnalign=center>
                           <mfrac>
                              <mrow>
                                 <msub>
                                    <mrow>
                                       <mi>Z</mi>
                                    </mrow>
                                    <mrow>
                                       <mn>2</mn>
                                       <mn>2</mn>
                                    </mrow>
                                 </msub>
                              </mrow>
                              <mrow>
                                 <msub>
                                    <mrow>
                                       <mi>Z</mi>
                                    </mrow>
                                    <mrow>
                                       <mn>2</mn>
                                       <mn>1</mn>
                                    </mrow>
                                 </msub>
                              </mrow>
                           </mfrac>
                        </mtd>
                     </mtr>
                  </mtable>
               </mrow>
               <mo fence=true form=postfix>)</mo>
            </mrow>
         </math>
      <td class=eq-no>(8)
</table>
<h3 class=sectionHead>Reciprocity and symmetry</h3>
<p>There is reciprocity when reversing transmitter and receiver give the same transfer function. Reciprocity is most often true in acoustic but it disappears in the presence of flow. There is symmetry when the quadrupole is unchanged by inversion. If there is symmetry, there is necessarily reciprocity. The conditions for reciprocity and symmetry on the various matrices are given in <a href=# data-img-src=${config.ASSETS_URL}/01/29/theory/table-1.svg data-img-alt="Table 1. Conditions for reciprocity and symmetry.">Table 1</a>.
<div class=table>
   <figure class=float>
      <figcaption class=caption>Table 1. Conditions for reciprocity and symmetry.</figcaption>
      <img src=${config.ASSETS_URL}/01/29/theory/table-1.svg alt="Table 1. Conditions for reciprocity and symmetry.">
   </figure>
</div>
<p>A chain of reciprocal quadrupoles is reciprocal, but a chain of symmetric quadrupoles is not necessarily symmetrical and a non-symmetrical quadrupole chain may be symmetrical!
<h3 class=sectionHead>Equivalent circuits</h3>
<p>
   Any reciprocal quadrupole may be represented by an equivalent electrical circuit. Two shapes are possible: the
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mn>Π</mn>
   </math>
   shape circuit and the
   <strong>
      <mstyle class=text>
         <mtext>T</mtext>
      </mstyle>
   </strong>
   shape circuit (see <a href="#" data-img-src=${config.ASSETS_URL}/01/29/theory/figure-3.svg data-img-alt="Figure 3. T-shape (left) and <math xmlns=http://www.w3.org/1998/Math/MathML display=inline><mi>Π</mi></math>-shape (right) equivalent circuits.">Figure 3</a>).
   <figure class=figure>
<p><img src=${config.ASSETS_URL}/01/29/theory/figure-3.svg alt="Figure 3. T-shape (left) and <math xmlns=http://www.w3.org/1998/Math/MathML display=inline><mi>Π</mi></math>-shape (right) equivalent circuits."><figcaption class=caption>Figure 3. T-shape (left) and <math xmlns=http://www.w3.org/1998/Math/MathML display=inline><mn>Π</mn></math>-shape (right) equivalent circuits.</figcaption></figure>
<p>
   The equivalent circuits have only 3 elements because the assumption of reciprocity removes one unknown. Link between the
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mstyle class=text>
         <mtext>T</mtext>
      </mstyle>
   </math>
   shape circuit and impedance matrix is given by
<table class=equation>
   <tr>
      <td style="font-size: 12px">
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <mrow>
               <mo fence=true form=prefix>(</mo>
               <mrow>
                  <mtable align=axis style="" equalrows=false columnlines="none none none none none none none none none" equalcolumns=false class=array>
                     <mtr>
                        <mtd class=array columnalign=center>
                           <msub>
                              <mrow>
                                 <mi>Z</mi>
                              </mrow>
                              <mrow>
                                 <mn>1</mn>
                                 <mn>1</mn>
                              </mrow>
                           </msub>
                        </mtd>
                        <mtd class=array columnalign=center>
                           <msub>
                              <mrow>
                                 <mi>Z</mi>
                              </mrow>
                              <mrow>
                                 <mn>1</mn>
                                 <mn>2</mn>
                              </mrow>
                           </msub>
                        </mtd>
                     </mtr>
                     <mtr>
                        <mtd class=array columnalign=center>
                           <msub>
                              <mrow>
                                 <mi>Z</mi>
                              </mrow>
                              <mrow>
                                 <mn>2</mn>
                                 <mn>1</mn>
                              </mrow>
                           </msub>
                        </mtd>
                        <mtd class=array columnalign=center>
                           <msub>
                              <mrow>
                                 <mi>Z</mi>
                              </mrow>
                              <mrow>
                                 <mn>2</mn>
                                 <mn>2</mn>
                              </mrow>
                           </msub>
                        </mtd>
                     </mtr>
                  </mtable>
               </mrow>
               <mo fence=true form=postfix>)</mo>
            </mrow>
            <mo class=MathClass-rel>=</mo>
            <mrow>
               <mo fence=true form=prefix>(</mo>
               <mrow>
                  <mtable align=axis style="" equalrows=false columnlines="none none none none none none none none none" equalcolumns=false class=array>
                     <mtr>
                        <mtd class=array columnalign=center>
                           <msub>
                              <mrow>
                                 <mi>Z</mi>
                              </mrow>
                              <mrow>
                                 <mn>1</mn>
                              </mrow>
                           </msub>
                           <mo class=MathClass-bin>+</mo>
                           <msub>
                              <mrow>
                                 <mi>Z</mi>
                              </mrow>
                              <mrow>
                                 <mi>T</mi>
                              </mrow>
                           </msub>
                        </mtd>
                        <mtd class=array columnalign=center>
                           <msub>
                              <mrow>
                                 <mi>Z</mi>
                              </mrow>
                              <mrow>
                                 <mi>T</mi>
                              </mrow>
                           </msub>
                        </mtd>
                     </mtr>
                     <mtr>
                        <mtd class=array columnalign=center>
                           <msub>
                              <mrow>
                                 <mi>Z</mi>
                              </mrow>
                              <mrow>
                                 <mi>T</mi>
                              </mrow>
                           </msub>
                        </mtd>
                        <mtd class=array columnalign=center>
                           <msub>
                              <mrow>
                                 <mi>Z</mi>
                              </mrow>
                              <mrow>
                                 <mn>2</mn>
                              </mrow>
                           </msub>
                           <mo class=MathClass-bin>+</mo>
                           <msub>
                              <mrow>
                                 <mi>Z</mi>
                              </mrow>
                              <mrow>
                                 <mi>T</mi>
                              </mrow>
                           </msub>
                        </mtd>
                     </mtr>
                  </mtable>
               </mrow>
               <mo fence=true form=postfix>)</mo>
            </mrow>
         </math>
      <td class=eq-no>(9)
</table>
<p>
   with
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>Z</mi>
         </mrow>
         <mrow>
            <mi>T</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <mn>1</mn>
      <mo class=MathClass-bin>∕</mo>
      <msub>
         <mrow>
            <mi>Y</mi>
         </mrow>
         <mrow>
            <mi>T</mi>
         </mrow>
      </msub>
   </math>
   and inversely
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>Z</mi>
         </mrow>
         <mrow>
            <mi>T</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <msub>
         <mrow>
            <mi>Z</mi>
         </mrow>
         <mrow>
            <mn>1</mn>
            <mn>2</mn>
         </mrow>
      </msub>
   </math>, <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>Z</mi>
         </mrow>
         <mrow>
            <mn>1</mn>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <msub>
         <mrow>
            <mi>Z</mi>
         </mrow>
         <mrow>
            <mn>1</mn>
            <mn>1</mn>
         </mrow>
      </msub>
      <mo class=MathClass-bin>−</mo>
      <msub>
         <mrow>
            <mi>Z</mi>
         </mrow>
         <mrow>
            <mn>1</mn>
            <mn>2</mn>
         </mrow>
      </msub>
   </math>
   and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>Z</mi>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <msub>
         <mrow>
            <mi>Z</mi>
         </mrow>
         <mrow>
            <mn>2</mn>
            <mn>2</mn>
         </mrow>
      </msub>
      <mo class=MathClass-bin>−</mo>
      <msub>
         <mrow>
            <mi>Z</mi>
         </mrow>
         <mrow>
            <mn>1</mn>
            <mn>2</mn>
         </mrow>
      </msub>
   </math>. The link between the
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mstyle class=text>
         <mtext>T</mtext>
      </mstyle>
   </math>
   shape circuit and transfer matrix is given by
<table class=equation>
   <tr>
      <td style="font-size: 10px">
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <mrow>
               <mo fence=true form=prefix>(</mo>
               <mrow>
                  <mtable align=axis style="" equalrows=false columnlines="none none none none none none none none none" equalcolumns=false class=array>
                     <mtr>
                        <mtd class=array columnalign=center>
                           <mi>A</mi>
                        </mtd>
                        <mtd class=array columnalign=center>
                           <mi>B</mi>
                        </mtd>
                     </mtr>
                     <mtr>
                        <mtd class=array columnalign=center>
                           <mi>C</mi>
                        </mtd>
                        <mtd class=array columnalign=center>
                           <mi>D</mi>
                        </mtd>
                     </mtr>
                  </mtable>
               </mrow>
               <mo fence=true form=postfix>)</mo>
            </mrow>
            <mo class=MathClass-rel>=</mo>
            <mrow>
               <mo fence=true form=prefix>(</mo>
               <mrow>
                  <mtable align=axis style="" equalrows=false columnlines="none none none none none none none none none" equalcolumns=false class=array>
                     <mtr>
                        <mtd class=array columnalign=center>
                           <mn>1</mn>
                           <mo class=MathClass-bin>+</mo>
                           <msub>
                              <mrow>
                                 <mi>Z</mi>
                              </mrow>
                              <mrow>
                                 <mn>1</mn>
                              </mrow>
                           </msub>
                           <msub>
                              <mrow>
                                 <mi>Y</mi>
                              </mrow>
                              <mrow>
                                 <mi>T</mi>
                              </mrow>
                           </msub>
                        </mtd>
                        <mtd class=array columnalign=center>
                           <msub>
                              <mrow>
                                 <mi>Z</mi>
                              </mrow>
                              <mrow>
                                 <mn>1</mn>
                              </mrow>
                           </msub>
                           <mo class=MathClass-bin>+</mo>
                           <msub>
                              <mrow>
                                 <mi>Z</mi>
                              </mrow>
                              <mrow>
                                 <mn>2</mn>
                              </mrow>
                           </msub>
                           <mo class=MathClass-bin>+</mo>
                           <msub>
                              <mrow>
                                 <mi>Z</mi>
                              </mrow>
                              <mrow>
                                 <mn>1</mn>
                              </mrow>
                           </msub>
                           <msub>
                              <mrow>
                                 <mi>Z</mi>
                              </mrow>
                              <mrow>
                                 <mn>2</mn>
                              </mrow>
                           </msub>
                           <msub>
                              <mrow>
                                 <mi>Z</mi>
                              </mrow>
                              <mrow>
                                 <mi>T</mi>
                              </mrow>
                           </msub>
                        </mtd>
                     </mtr>
                     <mtr>
                        <mtd class=array columnalign=center>
                           <msub>
                              <mrow>
                                 <mi>Y</mi>
                              </mrow>
                              <mrow>
                                 <mi>T</mi>
                              </mrow>
                           </msub>
                        </mtd>
                        <mtd class=array columnalign=center>
                           <mn>1</mn>
                           <mo class=MathClass-bin>+</mo>
                           <msub>
                              <mrow>
                                 <mi>Z</mi>
                              </mrow>
                              <mrow>
                                 <mn>2</mn>
                              </mrow>
                           </msub>
                           <msub>
                              <mrow>
                                 <mi>Y</mi>
                              </mrow>
                              <mrow>
                                 <mi>T</mi>
                              </mrow>
                           </msub>
                        </mtd>
                     </mtr>
                  </mtable>
               </mrow>
               <mo fence=true form=postfix>)</mo>
            </mrow>
         </math>
      <td class=eq-no>(10)
</table>
<p>
   and inversely
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>Y</mi>
         </mrow>
         <mrow>
            <mi>T</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <mi>C</mi>
   </math>
   ,
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>Z</mi>
         </mrow>
         <mrow>
            <mn>1</mn>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <mfrac>
         <mrow>
            <mi>A</mi>
            <mo class=MathClass-bin>−</mo>
            <mn>1</mn>
         </mrow>
         <mrow>
            <mi>C</mi>
         </mrow>
      </mfrac>
   </math>
   and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>Z</mi>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <mfrac>
         <mrow>
            <mi>D</mi>
            <mo class=MathClass-bin>−</mo>
            <mn>1</mn>
         </mrow>
         <mrow>
            <mi>C</mi>
         </mrow>
      </mfrac>
   </math>.
<h4 class=subsectionHead>Portion of line seen as a quadrupole</h4>
<p>
   A line portion can be seen as a quadrupole. Relationships
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msup>
         <mrow>
            <mi>p</mi>
         </mrow>
         <mrow>
            <mo class=MathClass-bin>+</mo>
         </mrow>
      </msup>
      <mo class=MathClass-open>(</mo>
      <mi>x</mi>
      <mo class=MathClass-close>)</mo>
      <mo class=MathClass-rel>=</mo>
      <msup>
         <mrow>
            <mi>p</mi>
         </mrow>
         <mrow>
            <mo class=MathClass-bin>+</mo>
         </mrow>
      </msup>
      <mo class=MathClass-open>(</mo>
      <mn>0</mn>
      <mo class=MathClass-close>)</mo>
      <msup>
         <mrow>
            <mi>e</mi>
         </mrow>
         <mrow>
            <mo class=MathClass-bin>−</mo>
            <mi>j</mi>
            <mi>k</mi>
            <mi>x</mi>
         </mrow>
      </msup>
   </math>
   and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msup>
         <mrow>
            <mi>p</mi>
         </mrow>
         <mrow>
            <mo class=MathClass-bin>−</mo>
         </mrow>
      </msup>
      <mo class=MathClass-open>(</mo>
      <mi>x</mi>
      <mo class=MathClass-close>)</mo>
      <mo class=MathClass-rel>=</mo>
      <msup>
         <mrow>
            <mi>p</mi>
         </mrow>
         <mrow>
            <mo class=MathClass-bin>−</mo>
         </mrow>
      </msup>
      <mo class=MathClass-open>(</mo>
      <mn>0</mn>
      <mo class=MathClass-close>)</mo>
      <msup>
         <mrow>
            <mi>e</mi>
         </mrow>
         <mrow>
            <mo class=MathClass-bin>+</mo>
            <mi>j</mi>
            <mi>k</mi>
            <mi>x</mi>
         </mrow>
      </msup>
   </math>
   can be written in a matrix form
<table class=equation>
   <tr>
      <td style="font-size: 11px">
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <mrow>
               <mo fence=true form=prefix>(</mo>
               <mrow>
                  <mtable align=axis style="" equalrows=false columnlines="none none none none none none none none none" equalcolumns=false class=array>
                     <mtr>
                        <mtd class=array columnalign=center>
                           <msup>
                              <mrow>
                                 <mi>p</mi>
                              </mrow>
                              <mrow>
                                 <mo class=MathClass-bin>−</mo>
                              </mrow>
                           </msup>
                           <mo class=MathClass-open>(</mo>
                           <mn>0</mn>
                           <mo class=MathClass-close>)</mo>
                        </mtd>
                     </mtr>
                     <mtr>
                        <mtd class=array columnalign=center>
                           <msup>
                              <mrow>
                                 <mi>p</mi>
                              </mrow>
                              <mrow>
                                 <mo class=MathClass-bin>+</mo>
                              </mrow>
                           </msup>
                           <mo class=MathClass-open>(</mo>
                           <mi>x</mi>
                           <mo class=MathClass-close>)</mo>
                        </mtd>
                     </mtr>
                  </mtable>
               </mrow>
               <mo fence=true form=postfix>)</mo>
            </mrow>
            <mo class=MathClass-rel>=</mo>
            <mrow>
               <mo fence=true form=prefix>(</mo>
               <mrow>
                  <mtable align=axis style="" equalrows=false columnlines="none none none none none none none none none" equalcolumns=false class=array>
                     <mtr>
                        <mtd class=array columnalign=center>
                           <mn>0</mn>
                        </mtd>
                        <mtd class=array columnalign=center>
                           <msup>
                              <mrow>
                                 <mi>e</mi>
                              </mrow>
                              <mrow>
                                 <mo class=MathClass-bin>−</mo>
                                 <mi>j</mi>
                                 <mi>k</mi>
                                 <mi>x</mi>
                              </mrow>
                           </msup>
                        </mtd>
                     </mtr>
                     <mtr>
                        <mtd class=array columnalign=center>
                           <msup>
                              <mrow>
                                 <mi>e</mi>
                              </mrow>
                              <mrow>
                                 <mo class=MathClass-bin>−</mo>
                                 <mi>j</mi>
                                 <mi>k</mi>
                                 <mi>x</mi>
                              </mrow>
                           </msup>
                        </mtd>
                        <mtd class=array columnalign=center>
                           <mn>0</mn>
                        </mtd>
                     </mtr>
                  </mtable>
               </mrow>
               <mo fence=true form=postfix>)</mo>
            </mrow>
            <mrow>
               <mo fence=true form=prefix>(</mo>
               <mrow>
                  <mtable align=axis style="" equalrows=false columnlines="none none none none none none none none none" equalcolumns=false class=array>
                     <mtr>
                        <mtd class=array columnalign=center>
                           <msup>
                              <mrow>
                                 <mi>p</mi>
                              </mrow>
                              <mrow>
                                 <mo class=MathClass-bin>+</mo>
                              </mrow>
                           </msup>
                           <mo class=MathClass-open>(</mo>
                           <mn>0</mn>
                           <mo class=MathClass-close>)</mo>
                        </mtd>
                     </mtr>
                     <mtr>
                        <mtd class=array columnalign=center>
                           <msup>
                              <mrow>
                                 <mi>p</mi>
                              </mrow>
                              <mrow>
                                 <mo class=MathClass-bin>−</mo>
                              </mrow>
                           </msup>
                           <mo class=MathClass-open>(</mo>
                           <mi>x</mi>
                           <mo class=MathClass-close>)</mo>
                        </mtd>
                     </mtr>
                  </mtable>
               </mrow>
               <mo fence=true form=postfix>)</mo>
            </mrow>
         </math>
      <td class=eq-no>(11)
</table>
<p>
   the matrix being here the scattering matrix with
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>R</mi>
         </mrow>
         <mrow>
            <mn>1</mn>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <msub>
         <mrow>
            <mi>R</mi>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <mn>0</mn>
   </math>
   and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>T</mi>
         </mrow>
         <mrow>
            <mn>1</mn>
            <mn>2</mn>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <msub>
         <mrow>
            <mi>T</mi>
         </mrow>
         <mrow>
            <mn>2</mn>
            <mn>1</mn>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <msup>
         <mrow>
            <mi>e</mi>
         </mrow>
         <mrow>
            <mo class=MathClass-bin>−</mo>
            <mi>j</mi>
            <mi>k</mi>
            <mi>x</mi>
         </mrow>
      </msup>
   </math>. It is verified that it is a symmetrical element since
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>T</mi>
         </mrow>
         <mrow>
            <mn>1</mn>
            <mn>2</mn>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <msub>
         <mrow>
            <mi>T</mi>
         </mrow>
         <mrow>
            <mn>2</mn>
            <mn>1</mn>
         </mrow>
      </msub>
   </math>
   and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>R</mi>
         </mrow>
         <mrow>
            <mn>1</mn>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <msub>
         <mrow>
            <mi>R</mi>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
      </msub>
   </math>. The transfer matrix
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mo class=MathClass-open>(</mo>
      <mi>P</mi>
      <mo class=MathClass-punc>,</mo>
      <mi>U</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   is then given by
<table class=equation>
   <tr>
      <td style="font-size: 11px">
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <mrow>
               <mo fence=true form=prefix>(</mo>
               <mrow>
                  <mtable align=axis style="" equalrows=false columnlines="none none none none none none none none none" equalcolumns=false class=array>
                     <mtr>
                        <mtd class=array columnalign=center>
                           <mi>A</mi>
                        </mtd>
                        <mtd class=array columnalign=center>
                           <mi>B</mi>
                        </mtd>
                     </mtr>
                     <mtr>
                        <mtd class=array columnalign=center>
                           <mi>C</mi>
                        </mtd>
                        <mtd class=array columnalign=center>
                           <mi>D</mi>
                        </mtd>
                     </mtr>
                  </mtable>
               </mrow>
               <mo fence=true form=postfix>)</mo>
            </mrow>
            <mo class=MathClass-rel>=</mo>
            <mrow>
               <mo fence=true form=prefix>(</mo>
               <mrow>
                  <mtable align=axis style="" equalrows=false columnlines="none none none none none none none none none" equalcolumns=false class=array>
                     <mtr>
                        <mtd class=array columnalign=center>
                           <mi class=qopname>cos</mi>
                           <mo>⁡</mo>
                           <mi>k</mi>
                           <mi>x</mi>
                        </mtd>
                        <mtd class=array columnalign=center>
                           <mi>j</mi>
                           <msub>
                              <mrow>
                                 <mi>Z</mi>
                              </mrow>
                              <mrow>
                                 <mi>c</mi>
                              </mrow>
                           </msub>
                           <mi class=qopname>sin</mi>
                           <mo>⁡</mo>
                           <mi>k</mi>
                           <mi>x</mi>
                        </mtd>
                     </mtr>
                     <mtr>
                        <mtd class=array columnalign=center>
                           <mi>j</mi>
                           <mi class=qopname>sin</mi>
                           <mo>⁡</mo>
                           <mi>k</mi>
                           <mi>x</mi>
                           <mo class=MathClass-bin>∕</mo>
                           <msub>
                              <mrow>
                                 <mi>Z</mi>
                              </mrow>
                              <mrow>
                                 <mi>c</mi>
                              </mrow>
                           </msub>
                        </mtd>
                        <mtd class=array columnalign=center>
                           <mi class=qopname>cos</mi>
                           <mo>⁡</mo>
                           <mi>k</mi>
                           <mi>x</mi>
                        </mtd>
                     </mtr>
                  </mtable>
               </mrow>
               <mo fence=true form=postfix>)</mo>
            </mrow>
         </math>
      <td class=eq-no>(12)
</table>
<p>
   Components of the equivalent T-shape circuit are
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>Y</mi>
         </mrow>
         <mrow>
            <mi>T</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <mi>j</mi>
      <mi class=qopname>sin</mi>
      <mo>⁡</mo>
      <mi>k</mi>
      <mi>x</mi>
      <mo class=MathClass-bin>∕</mo>
      <msub>
         <mrow>
            <mi>Z</mi>
         </mrow>
         <mrow>
            <mi>c</mi>
         </mrow>
      </msub>
   </math>
   and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>Z</mi>
         </mrow>
         <mrow>
            <mn>1</mn>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <msub>
         <mrow>
            <mi>Z</mi>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <mi>j</mi>
      <msub>
         <mrow>
            <mi>Z</mi>
         </mrow>
         <mrow>
            <mi>c</mi>
         </mrow>
      </msub>
      <mi class=qopname>tan</mi>
      <mo>⁡</mo>
      <mo class=MathClass-open>(</mo>
      <mi>k</mi>
      <mi>x</mi>
      <mo class=MathClass-bin>∕</mo>
      <mn>2</mn>
      <mo class=MathClass-close>)</mo>
   </math>.
   <h3 class=sectionHead>References</h3>
   <dl class=thebibliography>
     <dt>[1]
     <dd>L.J. Beranek. Acoustics. The American Institute of Physics, New York, 1986.
     <dt>[2]
     <dd>L. Brillouin. Wave propagation in periodic structures. MC Graw Hill, 1946.
     <dt>[3]
     <dd>A. Chaigne and J. Kergomard. Acoustics of Musical Instruments. Springer, New York, 2016.
     <dt>[4]
     <dd>M. L. Munjal. Acoustics of Ducts and Mufflers. Wiley, New York, 1987.
     <dt>[5]
     <dd>A.D. Pierce. Acoustics: an Introduction to its Physical Principles and Applications. MCGraw-Hill Book Company, Inc., New York, 1981.
     <dt>[6]
     <dd>M. Rossi.  ́Electroacoustique. Presses Polytechniques Romandes, Lausanne, 1986.
     <dt>[7]
     <dd>E. Skudrzyk. The Foundations of Acoustics—Basic Mathematics and Basic Acoustics. Springer–Verlag, 1971.
     <dt>[8]
     <dd>S. Temkin. Elements of Acoustics. Wiley, New York, 1981.
   </dl>`;

export default theory;
